import { useEffect, useState } from "react";
import { Check, MessageCircle, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, P } from "@/components/studio/portalKit";

const FIELDS = [["name", "Studio name *"], ["owner_name", "Owner name *"], ["gstin", "GSTIN (optional)"], ["district", "District *"],
  ["whatsapp", "WhatsApp number (login username) *"], ["email", "Email ID *"], ["billing_address", "Billing address *"], ["delivery_address", "Delivery address"]];
const EMPTY = { name: "", owner_name: "", gstin: "", district: "", whatsapp: "", email: "", billing_address: "", delivery_address: "", password: "", portal_active: true };

const StudioDialog = ({ row, onClose, onSaved }) => {
  const [v, setV] = useState(row ? { ...EMPTY, ...row, password: "" } : EMPTY);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await (row ? api.put(`${P}/central/studios/${row.id}`, v) : api.post(`${P}/central/studios`, v));
      toast.success(row ? "Studio updated" : "Studio login created"); onSaved({ ...v, id: data.id });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="studio-account-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>{row ? `Edit ${row.name}` : "Register a studio"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {FIELDS.map(([k, label]) => (
            <Field key={k} label={label}><Input data-testid={`studio-field-${k}`} value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} /></Field>
          ))}
          <Field label={row ? "New password (leave blank to keep)" : "Password * (not case-sensitive)"}>
            <Input data-testid="studio-field-password" type="password" autoComplete="new-password" value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} />
          </Field>
          <label className="flex items-center gap-3 pt-6 text-sm font-medium">
            <Switch data-testid="studio-field-active" checked={v.portal_active} onCheckedChange={(c) => setV({ ...v, portal_active: c })} />
            {v.portal_active ? "Active" : "Disabled"}
          </label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="studio-save-btn" disabled={busy} onClick={save} className="bg-crimson-500 hover:bg-crimson-600">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const welcome = (r, pw) => {
  const digits = String(r.whatsapp || "").replace(/\D/g, "").slice(-10);
  if (digits.length !== 10) { toast.error("A valid 10-digit WhatsApp number is needed"); return false; }
  const msg = `Hello ${r.owner_name || r.name}, welcome to VeloGuardX! Your studio account for ${r.name} is ready.\n` +
    `Open ${window.location.origin} and choose "Studio login".\nMobile: ${digits}\n` +
    (pw ? `Password: ${pw}\n` : "Password: the one shared by VeloGuardX\n") +
    "From there you can place material orders and register warranties, protection plans and inspection reports.";
  window.open(`https://wa.me/91${digits}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  return true;
};

const Stamp = ({ at, testid }) => (at
  ? <span data-testid={testid} className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700"><Check className="h-3.5 w-3.5" strokeWidth={3} />{fmtDate(at)}</span>
  : <span data-testid={testid} className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600"><span className="h-2 w-2 rounded-full bg-red-500" />Pending</span>);

export const DeskStudios = () => {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(undefined);
  const [q, setQ] = useState("");
  const [pw, setPw] = useState({});
  const [pending, setPending] = useState(false);
  const load = () => api.get(`${P}/central/studios`).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const sendWelcome = async (r, password) => {
    if (!welcome(r, password)) return;
    await api.post(`${P}/central/studios/${r.id}/welcome-sent`).catch(() => {});
    load();
  };
  const list = rows.filter((r) => `${r.name} ${r.owner_name} ${r.district} ${r.whatsapp}`.toLowerCase().includes(q.toLowerCase())
    && (!pending || !r.welcome_sent_at || !r.first_login_at));
  const todo = rows.filter((r) => !r.welcome_sent_at || !r.first_login_at).length;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input data-testid="studio-search" placeholder="Search studios" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        <button data-testid="onboarding-filter" onClick={() => setPending(!pending)}
          className={`min-h-[40px] rounded-full px-4 text-sm font-semibold transition-colors ${pending ? "bg-brand-deep text-white" : "bg-slate-100 text-slate-600"}`}>
          Onboarding pending ({todo})
        </button>
        <Button data-testid="add-studio-btn" onClick={() => setEdit(null)} className="ml-auto bg-crimson-500 hover:bg-crimson-600"><Plus className="mr-1.5 h-4 w-4" />Register studio</Button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-brand-line bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Studio</th><th>Owner</th><th>District</th><th>WhatsApp / login</th><th>Email</th><th>Welcome sent</th><th>First login</th><th>Status</th><th /></tr></thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.id} data-testid={`studio-row-${r.id}`} className="border-t border-slate-100">
                <td className="p-3 font-medium">{r.name}</td><td>{r.owner_name}</td><td>{r.district}</td>
                <td>{r.whatsapp || "—"}{!r.login_ready && <span className="ml-1 text-xs text-amber-700">(no password yet)</span>}</td><td>{r.email || "—"}</td>
                <td><Stamp at={r.welcome_sent_at} testid={`welcome-status-${r.id}`} /></td>
                <td><Stamp at={r.first_login_at} testid={`login-status-${r.id}`} /></td>
                <td><Badge className={`border-0 ${r.portal_active ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>{r.portal_active ? "Active" : "Disabled"}</Badge></td>
                <td className="whitespace-nowrap">
                  <Button size="sm" variant="ghost" data-testid={`welcome-studio-${r.id}`} onClick={() => sendWelcome(r, pw[r.whatsapp])} aria-label="Send welcome on WhatsApp"><MessageCircle className="h-3.5 w-3.5 text-emerald-600" /></Button>
                  <Button size="sm" variant="ghost" data-testid={`edit-studio-${r.id}`} onClick={() => setEdit(r)}><Pencil className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit !== undefined && <StudioDialog row={edit} onClose={() => setEdit(undefined)} onSaved={(v) => {
        const digits = String(v.whatsapp || "").replace(/\D/g, "").slice(-10);
        if (v.password) setPw((p) => ({ ...p, [digits]: v.password.toLowerCase() }));
        setEdit(undefined); load();
        if (!edit) toast("Send the welcome message?", { action: { label: "WhatsApp", onClick: () => sendWelcome({ ...v, whatsapp: digits }, v.password.toLowerCase()) }, duration: 15000 });
      }} />}
    </div>
  );
};
